import { Select, Button } from "@rio-cloud/rio-uikit";
import DatePicker from "@rio-cloud/rio-uikit/components/datepicker/DatePicker";
import { useIntl } from "react-intl";

import { formatOccurrenceStepLabel, OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { Loading } from "../../../utils/loading";
import { useKanbanWorkLog } from "../../../hooks/useWorkLog";
import { KanbanWorkLog, KanbanWorkLogFieldChange } from "../../../models/workLog";
import { formatWorklogFieldLabel } from "./form/utils/fields-labels";
import { formatStringToReadableValue } from "../../../utils/stringUtils";
import { formatDateTimeWithTimezone } from "../../../utils/dateUtils";

const Worklog = ({ occurrenceId }: { occurrenceId: string }) => {
  const intl = useIntl();

  const { workLogs, loading, loadMore, hasMoreData, filters, setFilters, resetPage, resolveEnumsLog } = useKanbanWorkLog(occurrenceId);
  
  const getFieldLabel = (fieldName: string): string => {
    return formatWorklogFieldLabel(intl, fieldName);
  };

  const logItem = (log: KanbanWorkLog, fieldChange: KanbanWorkLogFieldChange | undefined, hasFieldChanges: boolean) => {
    return (
      <div key={`${log.id}-${fieldChange?.id ?? 0}`} className="flex items-start justify-between padding-left-15 padding-bottom-40">
        <span className="w-1/12 text-size-18 bg-gray-100 dark:bg-neutral-700 rounded-circle width-30 height-30 flex items-center justify-center rioglyph rioglyph-ok mt-2"></span>

        <div className="w-full flex flex-col border border-gray-200 dark:border-neutral-700 ml-6 p-4 rounded-lg">
          <div className="w-full flex flex-row items-start">
            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.userLabel' })}</span>
              <span className="truncate">{log.userName}</span>
            </div>

            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.dateTimeLabel' })}</span>
              <span className="truncate">
                {formatDateTimeWithTimezone(log.changedAt, intl.locale)}
              </span>
            </div>

            <div className="w-2/12 flex flex-col padding-left-10 border-r">
              <span className="font-bold">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.stepLabel' })}</span>
              <span className="truncate">{formatOccurrenceStepLabel(intl, log.step)}</span>
            </div>

            <div className="w-6/12 flex flex-col padding-left-10">
              <span className="font-bold">{hasFieldChanges ? intl.formatMessage({ id: 'intl-msg:kanban.worklog.changedField' }) : intl.formatMessage({ id: 'intl-msg:kanban.worklog.description' })}</span>
              <span className="truncate">
                {hasFieldChanges
                  ? fieldChange?.fieldName === 'comment'
                    ? log.description
                    : getFieldLabel(fieldChange?.fieldName ?? '')
                  : log.description}
              </span>
            </div>
          </div>

            <div className="w-full flex flex-row mt-4">
              <div className="w-6/12 flex flex-col padding-left-10 border-r">
                <span className="font-bold">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.previousValue' })} </span>
                <div className="bg-gray-50 dark:bg-neutral-800 rounded-lg p-4 mr-4 break-words whitespace-pre-wrap">
                  {hasFieldChanges ? formatStringToReadableValue(resolveEnumsLog(fieldChange?.oldValue ?? '-')) : '-'}
                </div>
              </div>

              <div className="w-6/12 flex flex-col padding-left-10">
                <span className="font-bold">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.newValue' })}</span>
                <div className="bg-gray-50 dark:bg-neutral-800 rounded-lg p-4 mr-4 break-words whitespace-pre-wrap">
                  {hasFieldChanges ? formatStringToReadableValue(resolveEnumsLog(fieldChange?.newValue ?? '-')) : '-'}
                </div>
              </div>
            </div>
        </div>
      </div>
    )
  }

  return (
    <div className="history-assistance w-full">
      <div className="text-size-h2 font-medium">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.title' })}</div>
      <div className="text-size-h5 mb-8">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.subtitle' })}</div>

      <div className="filter-row flex flex-row items-end gap-6 mb-6">
        <div className="flex flex-col">
          <label htmlFor="step" className="mb-1 text-gray-700 dark:text-neutral-300">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.step' })}</label>
          <Select
            id="step"
            className="min-w-[180px] max-w-[220px]"
            placeholder={intl.formatMessage({ id: 'intl-msg:kanban.worklog.all' })}
            options={[
              { id: '', label: intl.formatMessage({ id: 'intl-msg:kanban.worklog.all' }) },
              ...Object.values(OccurrenceStepEnum).map((step) => ({
                id: step,
                label: formatOccurrenceStepLabel(intl, step),
              })),
            ]}
            onChange={(option) => 
              setFilters({ ...filters, step: option?.id ?? '' })}
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="user" className="mb-1 text-gray-700 dark:text-neutral-300">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.user' })}</label>
          <input
            id="user"
            type="text"
            className="min-w-[180px] max-w-[220px] dark:bg-neutral-800 dark:text-neutral-100"
            placeholder={intl.formatMessage({ id: 'intl-msg:kanban.worklog.userPlaceholder' })}
            onChange={(e) => 
              setFilters({ ...filters, user: e.target.value })}
          />
        </div>

        <div className="flex flex-col relative top-[14px]">
          <label className="mb-1 text-gray-700 dark:text-neutral-300">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.updateDate' })}</label>
          <div className="flex flex-row items-center gap-2">
            <DatePicker
              className="w-[200px] date-picker"
              id="update-date-start"
              locale={intl.locale}
              dateFormat="DD/MM/yyyy"
              onChange={(date) =>
                setFilters({ ...filters, dateStart: typeof date === 'string' ? date : date?.toDate()?.toISOString() ?? '',
              })
            }
              timeFormat="HH:mm"
      />
      <span className="text-gray-700 dark:text-neutral-300">{intl.formatMessage({ id: 'intl-msg:kanban.worklog.until' })}</span>
      <DatePicker
        className="w-[200px] date-picker"
        id="update-date-end"
        locale={intl.locale}
        dateFormat="DD/MM/yyyy"
          onChange={(date) => setFilters({ ...filters, dateEnd: typeof date === 'string' ? date : date?.toDate()?.toISOString() ?? '',})
        }
        timeFormat="HH:mm"
      />
    </div>
  </div>
        <div className="flex items-end">
          <Button
            className="min-w-[100px]"
            type="button"
            onClick={() => resetPage()}
          >
            {intl.formatMessage({ id: 'intl-msg:kanban.worklog.applyFilter' })}
          </Button>
        </div>
      </div>

      <>
        {loading ? (
          <div className="flex items-center justify-center mt-[80px]">
            <Loading label={intl.formatMessage({ id: 'intl-msg:kanban.worklog.loading' })} />
          </div>
        ) : (
          <section className="padding-top-20 vertical-divider">
            {(workLogs ?? []).map((log: KanbanWorkLog, logIndex: number) => {
              const hasFieldChanges = log.fieldChanges && log.fieldChanges.length > 0;
              if(hasFieldChanges) {
                return log.fieldChanges.map((fieldChange: KanbanWorkLogFieldChange, fieldIndex: number) => (
                  logItem(log, fieldChange, hasFieldChanges)
                ));
              } else {
                return logItem(log, undefined, hasFieldChanges);
              }
            })}

            {hasMoreData && <div
              className="LoadMore cursor-pointer text-center py-8 text-color-primary"
              onClick={() => loadMore()}
            >{intl.formatMessage({ id: 'intl-msg:monitoring.loadMore' })}</div>}
          </section>
        )}
      </>
    </div>
  );
};

export default Worklog;
